import { Injectable } from '@angular/core';
import { AnimationContext, StudioAnimation } from '../..';
import {
  TranslateAnimation,
  ZoomAnimation,
} from '../../../../animation/text-animation.service';

/**
 * Image Animation Presets
 *
 * Each preset defines a combination of zoom, translate, and mask properties
 * that create different visual effects for image animations.
 */
export interface ImageAnimationPreset {
  key: string;
  zoom?: ZoomAnimation;
  mask?: 'left' | 'right' | 'top' | 'bottom';
  duration?: number;
  translate?: TranslateAnimation;
}

@Injectable({ providedIn: 'root' })
export class StudioImageAnimationService {
  /**
   * Single image animation presets
   * Used when scene contains exactly one image
   */
  private singleImagePresets: Record<string, ImageAnimationPreset> = {
    'zoom-in': {
      key: 'zoom-in',
      zoom: { from: 0.8, to: 1 },
      duration: 1.5,
    },
    'zoom-out': {
      key: 'zoom-out',
      zoom: { from: 1.2, to: 1 },
      duration: 1.5,
    },
    // 'zoom-with-pan-right': {
    //   key: 'zoom-with-pan-right',
    //   zoom: { from: 0.9, to: 1.1, translate: [-50, 0] },
    //   duration: 2,
    // },
    // 'zoom-with-pan-left': {
    //   key: 'zoom-with-pan-left',
    //   zoom: { from: 0.9, to: 1.1, translate: [50, 0] },
    //   duration: 2,
    // },
    'translate-left': {
      key: 'translate-left',
      translate: { from: [-100, 0] },
      duration: 1.5,
    },
    'translate-right': {
      key: 'translate-right',
      translate: { from: [100, 0] },
      duration: 1.5,
    },
    'mask-from-left': {
      key: 'mask-from-left',
      mask: 'left',
      zoom: { from: 1, to: 1.05 },
      duration: 1.2,
    },
    'mask-from-right': {
      key: 'mask-from-right',
      mask: 'right',
      zoom: { from: 1, to: 1.05 },
      duration: 1.2,
    },
    'mask-from-top': {
      key: 'mask-from-top',
      mask: 'top',
      zoom: { from: 1.1, to: 1 },
      duration: 1.2,
    },
    'mask-from-bottom': {
      key: 'mask-from-bottom',
      mask: 'bottom',
      zoom: { from: 1.1, to: 1 },
      duration: 1.2,
    },
  };

  /**
   * Multi image animation presets
   * Used when scene contains two images (sequential animation)
   */
  private multiImagePresets: Record<string, ImageAnimationPreset[]> = {
    'sequential-zoom': [
      { key: 'first-zoom', zoom: { from: 0.8, to: 1 }, duration: 1 },
      { key: 'second-zoom', zoom: { from: 0.8, to: 1 }, duration: 1 },
    ],
    'cross-mask': [
      { key: 'first-mask-left', mask: 'left', duration: 0.8 },
      { key: 'second-mask-right', mask: 'right', duration: 0.8 },
    ],
    'opposing-pan': [
      {
        key: 'pan-left',
        zoom: { from: 0.95, to: 1.05, translate: [30, 0] },
        duration: 1.5,
      },
      {
        key: 'pan-right',
        zoom: { from: 0.95, to: 1.05, translate: [-30, 0] },
        duration: 1.5,
      },
    ],
    'staggered-reveal': [
      {
        key: 'first-mask-top',
        mask: 'top',
        zoom: { from: 1, to: 1.1 },
        duration: 1,
      },
      {
        key: 'second-mask-bottom',
        mask: 'bottom',
        zoom: { from: 1, to: 1.1 },
        duration: 1,
      },
    ],
  };

  constructor() {}

  /**
   * Get animation for image-based scenes
   * @param context - Animation context containing scene information
   * @returns StudioAnimation for the scene
   */
  getAnimation(context: AnimationContext): StudioAnimation | null {
    const { graphics, scene } = context;

    if (!graphics?.layout || graphics.layout.primaryElement !== 'image') {
      return null;
    }

    // Determine image count from context
    // We infer from contentDensity: 'sparse' = 1 image, 'balanced' = 2 images
    const isSingleImage = graphics.image?.count === 1;

    if (isSingleImage) {
      return this.getSingleImageAnimation(context);
    } else {
      return this.getMultiImageAnimation(context);
    }
  }

  /**
   * Generate animation for a single image scene
   */
  private getSingleImageAnimation(context: AnimationContext): StudioAnimation {
    const presetKeys = Object.keys(this.singleImagePresets);
    const selectedKey = this.getRandomFromArray(presetKeys);
    const preset = this.singleImagePresets[selectedKey];
    return this.buildAnimationFromPreset('image:0', preset);
  }

  /**
   * Generate animation for a multi-image scene (2 images)
   */
  private getMultiImageAnimation(context: AnimationContext): StudioAnimation {
    const presetKeys = Object.keys(this.multiImagePresets);
    const selectedKey = this.getRandomFromArray(presetKeys);
    const presets = this.multiImagePresets[selectedKey];

    // Build first image animation
    const firstAnimation = this.buildAnimationFromPreset('image:0', presets[0]);

    // Build second image animation and chain it
    const secondAnimation = this.buildAnimationFromPreset(
      'image:1',
      presets[1],
    );

    // Chain animations with overlap
    firstAnimation.next = {
      ...secondAnimation,
      overlap: 0.3, // Start second image slightly before first ends
    };

    return firstAnimation;
  }

  /**
   * Build a StudioAnimation from a preset configuration
   */
  private buildAnimationFromPreset(
    target: string,
    preset: ImageAnimationPreset,
  ): StudioAnimation {
    const animation: StudioAnimation = {
      target,
      type: 'appear',
      duration: preset.duration || 1,
    };

    if (preset.zoom) {
      (animation as any).zoom = preset.zoom;
    }

    if (preset.translate) {
      (animation as any).translate = preset.translate;
    }

    if (preset.mask) {
      animation.mask = preset.mask;
    }

    return animation;
  }

  /**
   * Get animation by specific preset key (for deterministic selection)
   */
  getAnimationByPreset(
    context: AnimationContext,
    presetKey: string,
  ): StudioAnimation | null {
    const { graphics } = context;

    if (!graphics?.layout || graphics.layout.primaryElement !== 'image') {
      return null;
    }

    // Check single image presets
    if (this.singleImagePresets[presetKey]) {
      return this.buildAnimationFromPreset(
        'image:0',
        this.singleImagePresets[presetKey],
      );
    }

    // Check multi-image presets
    if (this.multiImagePresets[presetKey]) {
      const presets = this.multiImagePresets[presetKey];
      const firstAnimation = this.buildAnimationFromPreset(
        'image:0',
        presets[0],
      );
      const secondAnimation = this.buildAnimationFromPreset(
        'image:1',
        presets[1],
      );
      firstAnimation.next = { ...secondAnimation, overlap: 0.3 };
      return firstAnimation;
    }

    return null;
  }

  /**
   * Get all available preset keys
   */
  getAvailablePresets(): { single: string[]; multi: string[] } {
    return {
      single: Object.keys(this.singleImagePresets),
      multi: Object.keys(this.multiImagePresets),
    };
  }

  private getRandomFromArray<T>(array: T[]): T {
    const randomIndex = Math.floor(Math.random() * array.length);
    return array[randomIndex];
  }
}
